import {
  dayPlus,
  dec,
  fermenterSteps,
  fillLimitLiters,
  fillsTank,
  staysOverlap,
  usualDaysIn,
} from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { ArrowRightLeft, Plus, TriangleAlert } from "lucide-react";
import { useId, useState } from "react";
import {
  type BrewDetail,
  send,
  type Tank,
  type TankStay,
  useBrew,
  useChange,
  useTanks,
} from "../api";
import { Dialog, DialogForm, ErrorNote, Field } from "../components/ui";
import { showDate, today } from "../format";
import { useNotify } from "../notify";
import { byKind, capacityLabel, inCellar, kindPlural, litersIn, totalLabel } from "../tankLayout";
import { type Sharing, sharingNote } from "../tankSchedule";

/**
 * A brew's tanks (Scott's Jam, 2026-10-03: "assigning batches to tanks when they're brewed so we know
 * that the tank is full"). A planned brew books one ahead; on brew day the fermenter is picked, and the
 * beer goes in when it is brewed; after that it moves to a brite tank, and is emptied when it is
 * packaged or dumped. Each step warns, and never stops, when the tank is already taken or too small.
 */
export function BrewTanks({ brew: b }: { brew: BrewDetail }) {
  const { data: tanks = [] } = useTanks();
  const [adding, setAdding] = useState(false);
  const [changing, setChanging] = useState<TankStay | null>(null);
  const [moving, setMoving] = useState<TankStay | null>(null);
  const [emptying, setEmptying] = useState<TankStay | null>(null);
  const cellar = inCellar(tanks);
  const current = b.stays.filter((s) => s.state !== "emptied");
  const past = b.stays.filter((s) => s.state === "emptied");
  const brewed = b.status === "brewed";
  const quick = useChange((tankId: string, key) => send.addStay({ brewId: b.id, tankId }, key));
  const pickId = useId();

  if (cellar.length === 0 && b.stays.length === 0) {
    return (
      <section className="brew-card">
        <p className="muted">
          No tanks set up yet. Add your fermenters and brite tanks under{" "}
          <Link to="/settings" hash="cellar">
            Settings, Equipment
          </Link>
          , and each brew can go in one.
        </p>
      </section>
    );
  }

  return (
    <section className="brew-card brew-tanks">
      {current.length === 0 && !brewed ? (
        // Brew day's one question: which fermenter it goes into.
        <div className="brew-field">
          <label htmlFor={pickId}>{b.status === "planned" ? "Book a tank" : "Into tank"}</label>
          <TankSelect
            id={pickId}
            tanks={cellar}
            brew={b}
            inOn={b.brewedOn}
            // Free or not for as long as it usually spends in a fermenter, which it goes into first.
            dueOutOn={dayPlus(b.brewedOn, b.tankDays.fermenter)}
            value=""
            disabled={quick.isPending}
            onChange={(id) => id && quick.mutate(id)}
          />
          <span className="faint">
            {b.status === "planned"
              ? "Booked from its brew day; it moves with it."
              : "The beer goes in when you press Brewed."}
          </span>
        </div>
      ) : null}
      {current.map((s) => (
        <StayRow
          key={s.id}
          stay={s}
          steps={stepsWhileIn(s, b.brewedOn, b.tankDays)}
          onChange={() => setChanging(s)}
          onMove={() => setMoving(s)}
          onEmpty={() => setEmptying(s)}
        />
      ))}
      {brewed && current.length === 0 ? (
        <p className="muted">{past.length ? "Out of its tanks." : "Not in a tank."}</p>
      ) : null}
      {past.length ? (
        <ul className="stay-history">
          {past.map((s) => (
            <li key={s.id}>
              {s.tank.name}, {showDate(s.inOn)} to {showDate(s.emptiedOn)}
              {s.dueOutOn && s.dueOutOn !== s.emptiedOn ? (
                <span className="faint"> (due out {showDate(s.dueOutOn)})</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {current.length > 0 || brewed ? (
        <div className="row">
          <button type="button" className="button" onClick={() => setAdding(true)}>
            <Plus size={16} /> {brewed && current.length === 0 ? "Put it in a tank" : "Add a tank"}
          </button>
        </div>
      ) : null}
      <ErrorNote error={quick.error} />
      {adding ? <StayDialog brew={b} tanks={cellar} onClose={() => setAdding(false)} /> : null}
      {changing ? (
        <StayDialog
          key={changing.id}
          brew={b}
          tanks={cellar}
          stay={changing}
          onClose={() => setChanging(null)}
        />
      ) : null}
      {moving ? (
        <MoveDialog
          key={moving.id}
          brew={b}
          tanks={cellar}
          stay={moving}
          onClose={() => setMoving(null)}
        />
      ) : null}
      {emptying ? (
        <EmptyDialog key={emptying.id} stay={emptying} onClose={() => setEmptying(null)} />
      ) : null}
    </section>
  );
}

/**
 * When a beer starts its dry hop and its lager/crash in a fermenter (Scott's Jam, 2026-10-08: "we need
 * a dry hop length in here as well"), counted from its brew day by its recipe's times: those that fall
 * while it is in this tank, as a line, or none.
 */
export function stepsWhileIn(
  s: Pick<TankStay, "tank" | "inOn" | "lastOn">,
  brewedOn: string,
  days: BrewDetail["tankDays"],
): string | null {
  if (s.tank.kind !== "Fermenter") return null;
  const { dryHopOn, lagerCrashOn } = fermenterSteps(brewedOn, days);
  const steps = [
    ["dry hop", dryHopOn],
    ["lager/crash", lagerCrashOn],
  ].flatMap(([step, on]) =>
    on && on >= s.inOn && (s.lastOn === null || on < s.lastOn)
      ? [`${step} from ${showDate(on)}`]
      : [],
  );
  const line = steps.join(", ");
  return line ? `${line[0]?.toUpperCase()}${line.slice(1)}` : null;
}

function StayRow({
  stay: s,
  steps,
  onChange,
  onMove,
  onEmpty,
}: {
  stay: TankStay;
  /** When it dry hops and lagers or crashes while in this tank, where it does. */
  steps: string | null;
  onChange: () => void;
  onMove: () => void;
  onEmpty: () => void;
}) {
  const due = s.dueOutOn ? `due out ${showDate(s.dueOutOn)}` : "no day out set";
  return (
    <div className="stay-row">
      <div className="stay-head">
        <strong>{s.tank.name}</strong>
        <span className="muted">{s.tank.kind}</span>
        {s.state === "in" ? (
          <span className="chip ok">In</span>
        ) : (
          <span className="chip plain">Booked</span>
        )}
        {s.overdue ? <span className="chip warn">Overdue</span> : null}
      </div>
      <span className="muted">
        {s.state === "in" ? `In since ${showDate(s.inOn)}` : `From ${showDate(s.inOn)}`}, {due}
      </span>
      {steps ? <span className="muted">{steps}</span> : null}
      <StayWarnings stay={s} />
      <div className="row">
        {s.state === "in" ? (
          <>
            <button type="button" className="button" onClick={onMove}>
              <ArrowRightLeft size={16} /> Move to
            </button>
            <button type="button" className="button" onClick={onEmpty}>
              Empty
            </button>
          </>
        ) : null}
        <button type="button" className="link-button" onClick={onChange}>
          Change
        </button>
      </div>
    </div>
  );
}

/**
 * What is wrong with a stay among the others in its tank, a line each: the tank full or scheduled for
 * another beer, too much for it, or out of service. Never a refusal (Scott's Jam, 2026-10-08: "you
 * should still allow me to start the brew so I don't have to stop what I'm doing during a work day and
 * reschedule everything"). A beer only scheduled whose brew has not started can be taken off the
 * calendar from the warning, so booking a tank today and dropping the brew it was meant for is done
 * here ("I should be able to schedule a brew to start today and then just delete the one above it if
 * I haven't already started brewing that day's batch").
 */
export function StayWarnings({ stay: s }: { stay: TankStay }) {
  const [takingOff, setTakingOff] = useState<string | null>(null);
  const notes: { key: string; text: string; planned: Sharing | null }[] = [
    ...s.doubleBooked.map((o) => ({
      key: o.stayId,
      text: sharingNote(s.tank.name, o),
      planned: o.brewStatus === "planned" ? o : null,
    })),
    ...(s.overCapacity
      ? [{ key: "over", text: `Too much for ${s.tank.name} at once`, planned: null }]
      : []),
    ...(s.outOfService
      ? [{ key: "out", text: `${s.tank.name} is out of service`, planned: null }]
      : []),
  ];
  if (notes.length === 0) return null;
  return (
    <>
      {notes.map((n) => (
        <p key={n.key} className="stay-warning">
          <TriangleAlert size={15} /> {n.text}.
          {n.planned ? (
            <button
              type="button"
              className="link-button danger-link stay-take-off"
              onClick={() => setTakingOff(n.planned?.brewId ?? null)}
            >
              Take {n.planned.recipeName} off the calendar
            </button>
          ) : null}
        </p>
      ))}
      {takingOff ? <TakeOffDialog brewId={takingOff} onClose={() => setTakingOff(null)} /> : null}
    </>
  );
}

/**
 * A brew not started taken off the calendar from where its tanks are shown (Scott's Jam, 2026-10-08:
 * "there needs to be an option to unschedule or cancel this brew on the screen for scheduling tanks"),
 * and from its own page. Its bookings go with it; nothing was taken from inventory for it.
 */
export function TakeOffDialog({
  open = true,
  brewId,
  onClose,
  onDone,
}: {
  /**
   * Shown. A page that keeps it mounted while shut keeps its idempotency key across a close and
   * reopen, so pressing again after an unanswered request is the same request.
   */
  open?: boolean;
  brewId: string;
  onClose: () => void;
  /** After it is off, as well as closing. */
  onDone?: () => void;
}) {
  const { data: b } = useBrew(brewId);
  const notify = useNotify();
  const remove = useChange((_: undefined, key) => send.deleteBrew(brewId, key), {
    onSuccess: () => {
      notify(`${b?.recipe.name ?? "The brew"} taken off the calendar`);
      onClose();
      onDone?.();
    },
  });
  const planned = b?.status === "planned";
  const tanks = [...new Set((b?.stays ?? []).map((x) => x.tank.name))];
  return (
    <Dialog open={open} onClose={onClose} title="Take it off the calendar?">
      <DialogForm
        onSubmit={() => planned && remove.mutate(undefined)}
        footer={
          <>
            <button type="button" className="button" onClick={onClose}>
              Keep it
            </button>
            {planned ? (
              <button type="submit" className="button danger" disabled={remove.isPending}>
                Take it off
              </button>
            ) : null}
          </>
        }
      >
        {!b ? (
          <p className="muted">Loading</p>
        ) : planned ? (
          <p>
            {b.recipe.name} on {showDate(b.brewedOn)} comes off the calendar
            {tanks.length
              ? `, and its ${tanks.length === 1 ? "booking" : "bookings"} in ${tanks.join(" and ")} with it`
              : ""}
            . Nothing was taken from inventory, so there is nothing to put back.
            {b.seriesId ? " The rest of its recurring brew stays on the calendar." : ""}
          </p>
        ) : (
          // Started since the warning was drawn: a brew in progress is deleted from its own page.
          <p>
            Brew day has started for {b.recipe.name}, so it stays.{" "}
            <Link to="/brews/$brewId" params={{ brewId: b.id }}>
              Open the brew
            </Link>{" "}
            to delete it there.
          </p>
        )}
        <ErrorNote error={remove.error} />
      </DialogForm>
    </Dialog>
  );
}

/**
 * The cellar's tanks to pick from, by kind, each saying whether it is free for the days asked: full or
 * scheduled for another beer then, which can still be picked, or out of service, which cannot.
 */
function TankSelect({
  id,
  "aria-describedby": describedBy,
  tanks,
  brew: b,
  inOn,
  dueOutOn,
  value,
  except,
  disabled,
  onChange,
}: {
  id?: string;
  /** Field passes it when it has a hint, as it passes the id. */
  "aria-describedby"?: string;
  tanks: Tank[];
  brew: BrewDetail;
  inOn: string;
  dueOutOn: string | null;
  value: string;
  /** A stay of this brew not to count against the tank: the one being changed or moved. */
  except?: string;
  disabled?: boolean;
  onChange: (tankId: string) => void;
}) {
  const span = { inOn, lastOn: dueOutOn ?? dayPlus(inOn, 1) };
  const busy = (t: Tank) => {
    const other = t.stays.find(
      (s) => s.id !== except && s.brew.id !== b.id && staysOverlap(span, s),
    );
    if (!other) return "";
    const what = other.brew.recipe.name;
    if (other.brew.recipe.name === b.recipe.name) return ` (${what} in it: a double batch)`;
    const until = other.lastOn ? ` until ${showDate(other.lastOn)}` : "";
    // Either can be picked (Scott's Jam, 2026-10-08).
    return fillsTank(other, other.brew)
      ? ` (full with ${what}${until})`
      : ` (scheduled for ${what}${until})`;
  };
  return (
    <select
      id={id}
      aria-describedby={describedBy}
      className="select"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Pick a tank</option>
      {byKind(tanks).map((g) => (
        <optgroup key={g.kind} label={kindPlural(g.kind)}>
          {g.tanks.map((t) => (
            <option key={t.id} value={t.id} disabled={t.outOfService}>
              {t.name}, {capacityLabel(t)}
              {t.outOfService ? " (out of service)" : busy(t)}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/**
 * What the brew puts in a tank, against what the tank takes: nine tenths of its total volume, else its
 * size (Scott, 2026-10-08).
 */
function TooBig({ brew: b, tank }: { brew: BrewDetail; tank: Tank | undefined }) {
  if (!tank) return null;
  const liters = b.readings.fermenterLiters ?? b.batchLiters;
  if (!dec(liters).round(0).gt(fillLimitLiters(tank).round(0))) return null;
  const amount = `${litersIn(liters, tank.capacity.unit)} ${tank.capacity.unit}`;
  return (
    <p className="stay-warning">
      <TriangleAlert size={15} />{" "}
      {tank.totalVolume === null
        ? `${amount} is more than ${tank.name} holds (${capacityLabel(tank)}).`
        : `${amount} leaves less than a tenth of ${tank.name}'s ${totalLabel(tank)} free.`}
    </p>
  );
}

/** Book a tank, put a brewed beer in one, or change a stay's tank or days. */
function StayDialog({
  brew: b,
  tanks,
  stay,
  onClose,
}: {
  brew: BrewDetail;
  tanks: Tank[];
  stay?: TankStay;
  onClose: () => void;
}) {
  const [tankId, setTankId] = useState(stay?.tank.id ?? "");
  const [inOn, setInOn] = useState(stay?.inOn ?? b.brewedOn);
  // Until a day is typed, a new stay is due out after the brew's usual days in a tank of that kind.
  const [typedDue, setDueOutOn] = useState<string | null>(stay ? (stay.dueOutOn ?? "") : null);
  const tank = tanks.find((t) => t.id === tankId);
  const usual = tank ? usualDaysIn(tank.kind, b.tankDays) : null;
  const dueOutOn = typedDue ?? (usual === null ? "" : dayPlus(inOn, usual));
  const brewed = b.status === "brewed";
  const save = useChange(
    (_: undefined, key) =>
      stay
        ? send.changeStay(
            {
              id: stay.id,
              patch: { tankId, inOn, dueOutOn: dueOutOn || null },
            },
            key,
          )
        : send.addStay(
            {
              brewId: b.id,
              tankId,
              inOn,
              dueOutOn: dueOutOn || null,
              // A brewed beer put in a tank today or before is in it; one ahead is a booking.
              filled: brewed && inOn <= today(),
            },
            key,
          ),
    { onSuccess: onClose },
  );
  const remove = useChange((_: undefined, key) => send.removeStay(stay?.id ?? "", key), {
    onSuccess: onClose,
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={
        stay ? `${stay.tank.name}, ${b.recipe.name}` : brewed ? "Put it in a tank" : "Book a tank"
      }
    >
      <DialogForm
        onSubmit={() => tankId && save.mutate(undefined)}
        footer={
          <>
            {stay ? (
              <button
                type="button"
                className="button danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate(undefined)}
                style={{ marginRight: "auto" }}
              >
                {stay.state === "booked" ? "Take the booking off" : "Not in this tank"}
              </button>
            ) : null}
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="button primary" disabled={!tankId || save.isPending}>
              {stay ? "Save" : brewed && inOn <= today() ? "Put it in" : "Book it"}
            </button>
          </>
        }
      >
        <Field label="Tank">
          <TankSelect
            tanks={tanks}
            brew={b}
            inOn={inOn}
            dueOutOn={dueOutOn || null}
            value={tankId}
            except={stay?.id}
            onChange={setTankId}
          />
        </Field>
        <TooBig brew={b} tank={tank} />
        <div className="field-pair">
          <Field label={stay?.state === "in" || (brewed && !stay) ? "In on" : "From"}>
            <input
              className="input"
              type="date"
              value={inOn}
              min={brewed ? undefined : b.brewedOn}
              onChange={(e) => e.target.value && setInOn(e.target.value)}
            />
          </Field>
          <Field
            label="Due out"
            hint={
              typedDue === null && usual !== null && tank
                ? `After its usual ${usual} ${usual === 1 ? "day" : "days"} in a ${tank.kind.toLowerCase()}`
                : "Leave empty if you don't know yet"
            }
          >
            <input
              className="input"
              type="date"
              value={dueOutOn}
              min={inOn}
              onChange={(e) => setDueOutOn(e.target.value)}
            />
          </Field>
        </div>
        <ErrorNote error={save.error ?? remove.error} />
      </DialogForm>
    </Dialog>
  );
}

/** A transfer, out of this tank and into another on one day: a fermenter to a brite tank, usually. */
function MoveDialog({
  brew: b,
  tanks,
  stay: s,
  onClose,
}: {
  brew: BrewDetail;
  tanks: Tank[];
  stay: TankStay;
  onClose: () => void;
}) {
  const booked = b.stays.find((x) => x.state === "booked");
  const [tankId, setTankId] = useState(booked?.tank.id ?? "");
  const [on, setOn] = useState(today());
  const bookedThere = b.stays.find((x) => x.state === "booked" && x.tank.id === tankId);
  // A booking there says when it is due out; without one, the brew's usual days in that kind of tank.
  const [typedDue, setDueOutOn] = useState<string | null>(null);
  const tank = tanks.find((t) => t.id === tankId);
  const usual = tank ? usualDaysIn(tank.kind, b.tankDays) : null;
  const dueOutOn = typedDue ?? bookedThere?.dueOutOn ?? (usual === null ? "" : dayPlus(on, usual));
  const move = useChange(
    (_: undefined, key) => send.moveStay({ id: s.id, tankId, on, dueOutOn: dueOutOn || null }, key),
    { onSuccess: onClose },
  );
  return (
    <Dialog open onClose={onClose} title={`Move ${b.recipe.name} out of ${s.tank.name}`}>
      <DialogForm
        onSubmit={() => tankId && move.mutate(undefined)}
        footer={
          <>
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="button primary" disabled={!tankId || move.isPending}>
              Move it
            </button>
          </>
        }
      >
        <Field label="To">
          <TankSelect
            tanks={tanks.filter((t) => t.id !== s.tank.id)}
            brew={b}
            inOn={on}
            dueOutOn={dueOutOn || null}
            value={tankId}
            onChange={(id) => {
              setTankId(id);
              setDueOutOn(null);
            }}
          />
        </Field>
        <TooBig brew={b} tank={tank} />
        <div className="field-pair">
          <Field label="On">
            <input
              className="input"
              type="date"
              value={on}
              min={s.inOn}
              max={today()}
              onChange={(e) => e.target.value && setOn(e.target.value)}
            />
          </Field>
          <Field label="Due out of it" hint="Leave empty if you don't know yet">
            <input
              className="input"
              type="date"
              value={dueOutOn}
              min={on}
              onChange={(e) => setDueOutOn(e.target.value)}
            />
          </Field>
        </div>
        <p className="faint" style={{ margin: 0 }}>
          {s.tank.name} is empty from that day.
          {bookedThere ? ` Its booking in ${bookedThere.tank.name} becomes the real thing.` : ""}
        </p>
        <ErrorNote error={move.error} />
      </DialogForm>
    </Dialog>
  );
}

/** The beer is out: packaged or dumped. The tank is free from that day. */
function EmptyDialog({ stay: s, onClose }: { stay: TankStay; onClose: () => void }) {
  const [on, setOn] = useState(today());
  const empty = useChange(
    (_: undefined, key) => send.changeStay({ id: s.id, patch: { emptiedOn: on } }, key),
    { onSuccess: onClose },
  );
  return (
    <Dialog open onClose={onClose} title={`Empty ${s.tank.name}?`}>
      <DialogForm
        onSubmit={() => empty.mutate(undefined)}
        footer={
          <>
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="button primary" disabled={empty.isPending}>
              Empty it
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          {s.brew.recipe.name} is out of {s.tank.name}: packaged or dumped. To move it to another
          tank, use Move to instead.
        </p>
        <Field label="On">
          <input
            className="input"
            type="date"
            value={on}
            min={s.inOn}
            max={today()}
            onChange={(e) => e.target.value && setOn(e.target.value)}
          />
        </Field>
        <ErrorNote error={empty.error} />
      </DialogForm>
    </Dialog>
  );
}
